import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CreditCard, Check, Lock, AlertTriangle } from 'lucide-react'
import { api } from '@/lib/api'

// 落地页定价配色（与 LandingPage Pricing 区块一致）
const CARBON = '#111111'
const PALE = '#D4D4D4'

/** OPT-034/040：设置页订阅卡片——两档（免费版/P1）并排对比（复刻落地页 Pricing 样式）+ 三类用量 + 升级 */
export function SubscriptionCard() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [upgrading, setUpgrading] = useState(false)
  const [error, setError] = useState('')

  const { data: current } = useQuery({ queryKey: ['subscription', 'current'], queryFn: () => api.getSubscriptionCurrent() })
  const { data: usage } = useQuery({ queryKey: ['subscription', 'usage'], queryFn: () => api.getSubscriptionUsage() })
  const { data: pricing } = useQuery({ queryKey: ['subscription', 'pricing'], queryFn: () => api.getSubscriptionPricing() })
  const { data: templates } = useQuery({ queryKey: ['package-templates'], queryFn: () => api.getPackageTemplates() })
  const { data: locations } = useQuery({ queryKey: ['appointment-presets', 'location'], queryFn: () => api.getAppointmentPresets('location') })

  const upgrade = useMutation({
    // OPT-040：仅可售两档
    mutationFn: (tier: 'free' | 'starter') => api.upgradeSubscription(tier),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscription'] })
      qc.invalidateQueries({ queryKey: ['clients'] })
      setUpgrading(false)
      setError('')
    },
    onError: (e) => setError(e instanceof Error ? e.message : t('common.failed')),
  })

  // OPT-040：只显示可售档位（free/starter；pro/studio 由后端标 sellable=false 过滤掉）
  const plans = (pricing || []).filter((p) => p.sellable && !p.comingSoon)
  const curClients = usage?.current ?? 0
  const maxClients = current?.maxClients ?? usage?.max ?? 0
  const maxTemplates = current?.maxTemplates ?? 0
  const maxLocations = current?.maxLocations ?? 0
  const templateCount = templates?.items?.length ?? 0
  const locationCount = locations?.items?.length ?? 0
  const clientsOver = curClients >= maxClients && maxClients > 0
  const templatesOver = maxTemplates > 0 && templateCount >= maxTemplates
  const locationsOver = maxLocations > 0 && locationCount >= maxLocations

  const usageRow = (label: string, cur: number, max: number, over: boolean) => (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={over ? 'text-red-600 font-medium flex items-center gap-1' : 'text-slate-700'}>
        {over && <AlertTriangle className="h-3.5 w-3.5" />}
        {cur}/{max}
      </span>
    </div>
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-4 w-4" /> {t('subscription.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 当前档位徽章 */}
        {current && (
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="default">{t(`subscription.tier.${current.tier}`)}</Badge>
            <span className="text-sm text-slate-600">${Number(current.priceMonthly).toFixed(2)} {t('common.perMonth')}</span>
            {/* OPT-055：福利期截止提示（付费档且带到期时间时显示） */}
            {current.tier !== 'free' && current.expiresAt && (
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                {t('subscription.promoUntil', { date: new Date(current.expiresAt * 1000).toISOString().slice(0, 10) })}
              </span>
            )}
          </div>
        )}

        {/* 三类用量（OPT-040） */}
        <div className="space-y-1.5 rounded-md bg-slate-50 p-3">
          {usageRow(t('subscription.clients'), curClients, maxClients, clientsOver)}
          {usageRow(t('subscription.templates'), templateCount, maxTemplates, templatesOver)}
          {usageRow(t('subscription.locations'), locationCount, maxLocations, locationsOver)}
        </div>

        {/* 两档并排对比（免费版 + P1，复刻落地页 Pricing 样式） */}
        {plans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            {plans.map((p) => {
              const dark = p.tier === 'starter'
              const isCurrent = p.tier === current?.tier
              return (
                <div
                  key={p.tier}
                  className="rounded-3xl p-6 flex flex-col gap-5 relative"
                  style={{ background: dark ? CARBON : '#F5F5F5', border: dark ? 'none' : `1px solid ${PALE}` }}
                >
                  {dark && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                      <span className="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide" style={{ background: '#10B981', color: '#fff' }}>
                        {t('landing.pricing.mostPopular')}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className={`text-[10px] font-semibold uppercase tracking-widest mb-2 ${dark ? 'text-white/35' : 'text-[#999]'}`}>
                      {t(`landing.pricing.${p.tier}.tagline`, { defaultValue: p.tagline })}
                    </p>
                    <h3 className="leading-none mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color: dark ? '#fff' : CARBON, letterSpacing: '0.02em' }}>
                      {t(`subscription.tier.${p.tier}`)}
                    </h3>
                    <div className="flex items-end gap-1">
                      <span className="leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '48px', color: dark ? '#fff' : CARBON, letterSpacing: '-0.02em' }}>
                        ${Number(p.priceMonthly).toFixed(2)}
                      </span>
                      <span className={`text-sm pb-2 ${dark ? 'text-white/35' : 'text-[#999]'}`}>{t('common.perMonth')}</span>
                    </div>
                  </div>
                  <div style={{ height: 1, background: dark ? 'rgba(255,255,255,0.08)' : PALE }} />
                  <ul className="flex flex-col gap-2.5 flex-1">
                    {p.features.map((f, i) => (
                      <li key={f} className="flex items-center gap-2.5">
                        <span className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(17,17,17,0.08)' }}>
                          {p.available?.[i] ? (
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><polyline points="1.5,4 3,5.5 6.5,2" stroke={dark ? '#10B981' : CARBON} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                          ) : (
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><line x1="2" y1="4" x2="6" y2="4" stroke={dark ? '#6B7280' : '#9CA3AF'} strokeWidth="1.4" strokeLinecap="round" /></svg>
                          )}
                        </span>
                        <span className={`text-sm ${dark ? (p.available?.[i] ? 'text-white/65' : 'text-white/30') : (p.available?.[i] ? 'text-[#555]' : 'text-[#999]')}`}>
                          {t(`subscription.${f}`, { defaultValue: f })}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <button
                    className="w-full rounded-full py-3 text-sm font-semibold transition-all hover:opacity-85"
                    style={{
                      background: isCurrent ? '#E5E7EB' : (dark ? '#ffffff' : CARBON),
                      color: isCurrent ? '#9CA3AF' : (dark ? CARBON : '#ffffff'),
                      cursor: isCurrent ? 'not-allowed' : 'pointer',
                    }}
                    disabled={isCurrent}
                    onClick={() => {
                      if (window.confirm(t('subscription.confirmUpgrade', { tier: t(`subscription.tier.${p.tier}`) }))) {
                        setUpgrading(true)
                        upgrade.mutate(p.tier as 'free' | 'starter')
                      }
                    }}
                  >
                    {isCurrent ? t('subscription.currentTier') : (upgrading ? t('common.loading') : t('subscription.upgrade'))}
                  </button>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-400">{t('subscription.noPlans')}</p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {/* OPT-055：福利升级为彩蛋——页面不做任何明面提示，点击升级的人才会在结果里发现福利期徽章 */}
      </CardContent>
    </Card>
  )
}
